import React, { useEffect } from 'react';
import { loadRecurly, RecurlyWrapper, LegacyCaptchaContainer } from '@fff-web/fff-payments';
import { ProspectFooter } from '@fff-web/fff-global-footer';
import Toast, { showWarningToast } from '@fff-web/fff-ui-shared/Toast';
import LoadingSpinner from '@fff-web/fff-ui-shared/LoadingSpinner';

import { useAppContext } from '../../utils/ContextProviders/AppContext';
import { useSharedContext } from '../../pages/GTBCustomize/ContextProviders/SharedContext';
import { useGTBCheckoutContext } from '../../pages/GTBCustomize/ContextProviders/GTBContext';
import { usePublicShopContext } from '../../pages/GTBCustomize/ContextProviders/PublicShopContext';

import YourChoices from '../YourChoices';
import CreateAccount from '../CreateAccount';
import PaymentFormContainer from '../../pages/GTBCustomize/components/PaymentForm/PaymentFormContainer';
import OrderSummaryContainer from '../../pages/GTBCustomize/components/OrderSummary/OrderSummaryContainer';
import AdditionalOrderSummary from '../AdditionalOrderSummary/AdditionalOrderSummary';
import OrderButton from '../../pages/GTBCustomize/components/OrderButton';
import RegisterUser from '../RegisterUser/RegisterUser';
import { warningToastContent, warningToastTitle } from '../../utils/constants/checkout';
import { checkShippingRestrictions } from './utils/checkoutUtils';
import EXPERIENCE_FLOW from '../../utils/constants/experienceFlow';
import styles from './Checkout.module.scss';
import ShippingLocationForm from '../ShippingLocationForm';
import IntroProductRegister from '../RegisterUser/IntroProductRegister';
import { ValidateOnMount } from '../CreateAccount/props/inputProps';

type Flags = {
  enableApplePaySocialEcomm: boolean,
};

type Props = {
  title: string;
  flags?: Flags;
  emailDisabled?: boolean,
  focusField?: string;
  validateOnMount?: ValidateOnMount;
};

const Checkout = ({
  title, flags, emailDisabled, focusField, validateOnMount,
}: Props) => {
  const { recurlyLoaded } = useAppContext();
  const {
    experienceFlow,
    loading,
    prospect: {
      address: { shipCountry },
    },
  } = useSharedContext();
  const isPublicShop = experienceFlow === EXPERIENCE_FLOW.publicShop;
  const isGTB = experienceFlow === EXPERIENCE_FLOW.gtb;
  const useContext = isPublicShop ? usePublicShopContext : useGTBCheckoutContext;
  const { choices, cartId } = useContext();

  useEffect(() => {
    loadRecurly();
  }, []);

  useEffect(() => {
    if (checkShippingRestrictions(choices, shipCountry)) {
      showWarningToast(warningToastTitle, warningToastContent);
    }
  }, [shipCountry]);

  return (
    <>
      <h1 className={styles.coTitle}>{title}</h1>
      <div className={styles.coBody}>
        { loading && (
          <div className={styles.loadingWrapper}>
            <LoadingSpinner isFullPage={false} />
          </div>
        )}
        { recurlyLoaded && (
          <RecurlyWrapper>
            <section className={styles.coCustomize}>
              <YourChoices choices={choices} />
              <CreateAccount
                emailDisabled={emailDisabled}
                focusField={focusField}
                validateOnMount={validateOnMount}
              />
              <ShippingLocationForm />
              <PaymentFormContainer flags={flags} />
            </section>
            <section className={styles.coOrderSummary}>
              <OrderSummaryContainer cartId={cartId} />
              {isGTB && <AdditionalOrderSummary />}
              <OrderButton />
            </section>
          </RecurlyWrapper>
        )}
      </div>
      <ProspectFooter />
      {isPublicShop
        ? <IntroProductRegister />
        : <RegisterUser />}
      <LegacyCaptchaContainer />
      <Toast />
    </>
  );
};

Checkout.defaultProps = {
  flags: {
    enableApplePaySocialEcomm: false,
  },
  emailDisabled: false,
  focusField: '',
  validateOnMount: 'none',
};

export default Checkout;
